// Hoja de "objeciones graves" del email. Aparece SOLO al intentar continuar con
// un email problemático (cuenta existente / typo de dominio / dominio que no
// recibe) — el hint inline nunca bloquea; esto es la escalación al submit.
//
// Bottom-sheet en mobile, diálogo centrado en desktop (ver .obj-* en index.css).
// Convierte el callejón en atajo: "Ingresar con este email", "Recuperar
// contraseña", "Usar la sugerencia". "Modificar" devuelve el foco al campo de
// email. Foco atrapado + Escape, como corresponde a un modal.

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { TypoBoard, ColoredDomain } from '@/components/EmailTypoSuggestion';
import { btn } from '@/lib/button-styles';
import type { DomainSuggestion } from '@/lib/emailTypo';

export type ObjectionVariant = 'exists' | 'undeliverable' | 'typo';

interface Props {
  variant: ObjectionVariant | null; // null = cerrada
  partnerId: string;
  domain?: string;
  suggestion?: DomainSuggestion | null;
  onModify: () => void;
  onAcceptTypo: () => void;
  onKeepTypo: () => void;
  onClose: () => void;
}

const IconWarn = (): JSX.Element => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.2 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.2a2 2 0 0 0-3.4 0Z" /></svg>
);
const IconInfo = (): JSX.Element => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></svg>
);

// Mismas clases que el boton de submit del formulario que quedo atras (radio,
// tipografia, foco). Lo unico propio de la hoja es el `w-full`: son botones
// apilados, no una fila.
const btnPrimary = btn('primary', 'md', 'w-full');
const btnDefault = btn('secondary', 'md', 'w-full');
const btnGhost = btn('ghost', 'md', 'w-full');

export default function EmailObjectionSheet({
  variant,
  partnerId,
  domain,
  suggestion,
  onModify,
  onAcceptTypo,
  onKeepTypo,
  onClose,
}: Props): JSX.Element | null {
  const sheetRef = useRef<HTMLDivElement>(null);
  const search = typeof window !== 'undefined' ? window.location.search : '';

  // Foco inicial + trap de Tab. NO hay Escape-para-cerrar: la objeción se responde
  // con un botón, no se descarta — el foco queda atrapado hasta elegir una opción.
  useEffect(() => {
    if (!variant) return undefined;
    const node = sheetRef.current;
    const focusables = node?.querySelectorAll<HTMLElement>('a[href], button');
    focusables?.[0]?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Tab' && focusables && focusables.length > 0) {
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [variant]);

  // Click afuera: NO cierra. Un shake sutil avisa que hay que elegir una opción
  // (respeta prefers-reduced-motion). WAAPI para no pelear con la animación de
  // entrada del sheet.
  const nudge = (): void => {
    const el = sheetRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-7px)' },
        { transform: 'translateX(7px)' },
        { transform: 'translateX(-5px)' },
        { transform: 'translateX(5px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 380, easing: 'ease-in-out' },
    );
  };

  if (!variant) return null;

  const isWarn = variant !== 'typo';
  const suggestedDomain = suggestion?.suggestedDomain ?? '';
  const typedDomain = suggestion?.typedDomain ?? domain ?? '';

  return (
    <div
      className="obj-back"
      onMouseDown={(e) => {
        // No se cierra tocando afuera: hay que responder la objeción con un botón.
        if (e.target === e.currentTarget) nudge();
      }}
    >
      <div
        ref={sheetRef}
        className="obj-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="obj-title"
      >
        <div className="obj-grab" />
        <div
          className={`grid place-items-center h-16 w-16 rounded-2xl mb-4 ${
            isWarn ? 'bg-error/15 text-error' : 'bg-accent/15 text-accent'
          }`}
        >
          {isWarn ? <IconWarn /> : <IconInfo />}
        </div>

        {variant === 'exists' ? (
          <>
            <h4 id="obj-title" className="text-lg font-semibold mb-2">
              Ya tenés una cuenta
            </h4>
            <p className="text-sm text-text-muted mb-[18px]">Este correo ya tiene cuenta.</p>
            <div className="flex flex-col gap-2.5">
              <Link to={`/${partnerId}/login${search}`} className={btnPrimary}>
                <span>Ingresar</span>
              </Link>
              <Link to={`/${partnerId}/forgot-password${search}`} className={btnDefault}>
                <span>Recuperar contraseña</span>
              </Link>
              <button type="button" onClick={onModify} className={btnGhost}>
                <span>Editar el correo</span>
              </button>
            </div>
          </>
        ) : null}

        {variant === 'undeliverable' ? (
          <>
            <h4 id="obj-title" className="text-lg font-semibold mb-2">
              Esa dirección de correo NO existe
            </h4>
            <p className="text-sm text-text-muted mb-[18px]">Ahí no te va a llegar el mail.</p>
            <div className="flex flex-col gap-2.5">
              <button type="button" onClick={onModify} className={btnPrimary}>
                <span>Editar el correo</span>
              </button>
            </div>
          </>
        ) : null}

        {variant === 'typo' ? (
          <>
            <h4 id="obj-title" className="text-lg font-semibold mb-2">
              ¿Revisamos la dirección de correo?
            </h4>
            <p className="text-sm text-text-muted mb-3">
              ¿Quisiste poner <ColoredDomain typed={typedDomain} suggested={suggestedDomain} />?
            </p>
            {suggestion ? (
              <div className="mb-[18px]">
                <TypoBoard typed={typedDomain} suggested={suggestedDomain} />
              </div>
            ) : null}
            <div className="flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => {
                  onAcceptTypo();
                  onClose();
                }}
                className={btnPrimary}
              >
                <span>Usar {suggestedDomain}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  onKeepTypo();
                  onClose();
                }}
                className={btnDefault}
              >
                <span>Está bien así</span>
              </button>
              <button type="button" onClick={onModify} className={btnGhost}>
                <span>Editar el correo</span>
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
